import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Image from "@/components/ui/optimized-image";
import { Input } from "@/components/ui/input";
import { searchClasses, searchProfiles, type ProfileSearchResult } from "@/lib/search";
import type { Class } from "@/types";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { useI18n } from "@/lib/i18n";

type ClassSearchResult = Class & {
  courseTitle?: string;
  instructorUsername?: string | null;
};

export function SearchBar() {
  const [query, setQuery] = useState("");
  const [profileResults, setProfileResults] = useState<ProfileSearchResult[]>([]);
  const [classResults, setClassResults] = useState<ClassSearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useI18n();

  useEffect(() => {
    const searchTerm = query.trim();

    if (searchTerm.length === 0) {
      setProfileResults([]);
      setClassResults([]);
      setIsOpen(false);
      setIsLoading(false);
      return;
    }

    setIsOpen(true);
    setIsLoading(true);

    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const [profiles, classes] = await Promise.all([
          searchProfiles(searchTerm),
          searchClasses(searchTerm),
        ]);
        if (!cancelled) {
          setProfileResults(profiles);
          setClassResults(classes as ClassSearchResult[]);
        }
      } catch {
        if (!cancelled) {
          setProfileResults([]);
          setClassResults([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query]);

  // Keyboard shortcut: Cmd/Ctrl + K to focus search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === "Escape") {
        setIsOpen(false);
        setQuery("");
        inputRef.current?.blur();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleProfileClick = (profile: ProfileSearchResult) => {
    navigate(`/profile/${profile.username || profile.id}`);
    setQuery("");
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleClassClick = (classId: string) => {
    navigate(`/classroom/${classId}`);
    setQuery("");
    setIsOpen(false);
    inputRef.current?.blur();
  };

  return (
    <div ref={searchRef} className="relative w-full max-w-md">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder={t("search.placeholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => {
            if (query.trim().length > 0) {
              setIsOpen(true);
            }
          }}
          onBlur={() => {
            // Delay to allow click events on results
            setTimeout(() => {
              setIsOpen(false);
            }, 200);
          }}
          className="pl-10 pr-4"
        />
        {query.length === 0 && (
          <kbd className="pointer-events-none absolute right-3 top-1/2 hidden h-5 -translate-y-1/2 select-none items-center gap-1 rounded border border-border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
            <span className="text-xs">⌘</span>K
          </kbd>
        )}
      </div>
      {isOpen && query.trim().length > 0 && (
        <div className="absolute top-full z-50 mt-2 w-full rounded-lg border border-border bg-background shadow-lg">
          <div className="max-h-96 overflow-y-auto p-2">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <p className="text-sm text-muted-foreground">{t("search.searching")}</p>
              </div>
            ) : profileResults.length > 0 || classResults.length > 0 ? (
              <div className="space-y-3">
                {profileResults.length > 0 && (
                  <div className="space-y-1">
                    <p className="px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {t("search.profiles")}
                    </p>
                    {profileResults.map((profile) => {
                      const displayName =
                        profile.username ||
                        [profile.first_name, profile.last_name].filter(Boolean).join(" ") ||
                        t("search.userFallback", { id: profile.id.slice(0, 8) });
                      const initials = displayName.charAt(0).toUpperCase();

                      return (
                        <button
                          key={profile.id}
                          onClick={() => handleProfileClick(profile)}
                          onMouseDown={(e) => {
                            e.preventDefault();
                          }}
                          className={cn(
                            "w-full rounded-md border border-transparent p-3 text-left transition-all duration-200 hover:bg-muted hover:border-border hover:scale-[1.02] active:scale-[0.98]"
                          )}
                        >
                          <div className="flex items-center gap-3">
                            {profile.image_url ? (
                              <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-full">
                                <Image
                                  src={profile.image_url}
                                  alt={displayName}
                                  fill
                                  sizes="40px"
                                  className="object-cover"
                                />
                              </div>
                            ) : (
                              <div className="h-10 w-10 shrink-0 rounded-full bg-muted flex items-center justify-center text-sm font-medium">
                                {initials}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{displayName}</p>
                              {profile.username && (
                                <p className="text-xs text-muted-foreground">@{profile.username}</p>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {classResults.length > 0 && (
                  <div className="space-y-1">
                    <p className="px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {t("search.classes")}
                    </p>
                    {classResults.map((cls) => {
                      const sessionDate = cls.sessionDate
                        ? new Date(cls.sessionDate as unknown as string).toLocaleDateString()
                        : null;
                      return (
                        <button
                          key={cls.id}
                          onClick={() => handleClassClick(cls.id)}
                          onMouseDown={(e) => {
                            e.preventDefault();
                          }}
                          className={cn(
                            "w-full rounded-md border border-transparent p-3 text-left transition-all duration-200 hover:bg-muted hover:border-border hover:scale-[1.02] active:scale-[0.98]"
                          )}
                        >
                          <div className="flex items-start gap-3">
                            <div className="h-10 w-10 shrink-0 rounded-md bg-muted flex items-center justify-center text-xs font-medium">
                              {t("search.class")}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium line-clamp-1">{cls.title}</p>
                              <p className="text-xs text-muted-foreground line-clamp-1">
                              {cls.courseTitle || t("search.classroom")}
                              </p>
                              {sessionDate && (
                                <p className="text-[11px] text-muted-foreground mt-1">
                                  {sessionDate}
                                </p>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <p className="text-sm text-muted-foreground">
                  {t("search.noResults")}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("search.tryDifferent")}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

